import Drawer from "devextreme-react/drawer";
import ScrollView from "devextreme-react/scroll-view";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Header, SideNavigationMenu, Footer } from "../../components";
import "./side-nav-outer-toolbar.scss";
import { useScreenSize } from "../../utils/media-query";
import { Template } from "devextreme-react/core/template";
import { useMenuPatch } from "../../utils/patches";
import { Popup, ToolbarItem } from "devextreme-react/popup";

export default function ({ title, children, togglePopUp }) {
  const scrollViewRef = useRef();
  const history = useHistory();
  const { isXSmall, isLarge } = useScreenSize();
  const [patchCssClass, onMenuReady] = useMenuPatch();
  const [menuStatus, setMenuStatus] = useState(
    isLarge ? MenuStatus.Opened : MenuStatus.Closed
  );
  const [popUp, setPopUp] = useState(false);
  const [urlPath, setUrlPath] = useState("");

  const closeButtonFunc = () => {
    setPopUp(false);
  };

  const switchButtonFunc = () => {
    setPopUp(false);
    history.push(urlPath);
  };

  const closeButtonOptions = {
    text: "הישאר",
    stylingMode: "outlined",
    onClick: closeButtonFunc,
  };

  const switchButtonOptions = {
    text: " יציאה מהמסך הנוכחי",
    stylingMode: "contained",
    onClick: switchButtonFunc,
  };

  const toggleMenu = useCallback(({ event }) => {
    setMenuStatus((prevMenuStatus) =>
      prevMenuStatus === MenuStatus.Closed
        ? MenuStatus.Opened
        : MenuStatus.Closed
    );
    event.stopPropagation();
  }, []);

  const temporaryOpenMenu = useCallback(() => {
    setMenuStatus((prevMenuStatus) =>
      prevMenuStatus === MenuStatus.Closed
        ? MenuStatus.TemporaryOpened
        : prevMenuStatus
    );
  }, []);

  const onOutsideClick = useCallback(() => {
    setMenuStatus((prevMenuStatus) =>
      prevMenuStatus !== MenuStatus.Closed && !isLarge
        ? MenuStatus.Closed
        : prevMenuStatus
    );
  }, [isLarge]);

  // Reset the shared ScrollView to the top on EVERY route change. The ScrollView
  // instance lives in this layout and persists across routes, keeping its scroll
  // position — so navigating from a scrolled-down page (e.g. the bottom of a long
  // form → "document sent" screen, then back to the form) would otherwise leave the
  // next page scrolled down with its header cut off. (Menu clicks already reset via
  // onNavigationChanged; this covers programmatic navigation too.)
  useEffect(() => {
    const resetScroll = () => scrollViewRef.current?.instance?.scrollTo(0);
    // history.listen covers react-router navigation; hashchange covers direct
    // `window.location.hash = ...` navigation (used when a form is sent → #/signatures).
    const unlisten = history.listen(resetScroll);
    window.addEventListener("hashchange", resetScroll);
    return () => {
      unlisten();
      window.removeEventListener("hashchange", resetScroll);
    };
  }, [history]);

  const onNavigationChanged = useCallback(
    ({ itemData: { path }, event, node }) => {
      if (menuStatus === MenuStatus.Closed || !path || node.selected) {
        event.preventDefault();
        return;
      }
      const allowed = ["#/home", "#/drafts", "#/templates", "#/tasks"];
      const hash = window.location.hash;
      const currentPath = hash.substring(1);

      if (!allowed.includes(hash) && currentPath !== path && !hash.includes("/signatures")) {
        setPopUp(true);
        setUrlPath(path);
      } else {
        history.push(path);
      }
      scrollViewRef.current.instance.scrollTo(0);

      if (!isLarge || menuStatus === MenuStatus.TemporaryOpened) {
        setMenuStatus(MenuStatus.Closed);
        event.stopPropagation();
      }
    },
    [history, menuStatus, isLarge, setPopUp]
  );

  return (
    <div className={"side-nav-outer-toolbar"}>
      <Header
        className={"layout-header"}
        menuToggleEnabled
        toggleMenu={toggleMenu}
        title={title}
      />
      <Drawer
        className={["drawer", patchCssClass].join(" ")}
        position={"before"}
        closeOnOutsideClick={onOutsideClick}
        openedStateMode={isLarge ? "shrink" : "overlap"}
        revealMode={isXSmall ? "slide" : "expand"}
        minSize={isXSmall ? 0 : 60}
        maxSize={250}
        shading={isLarge ? false : true}
        opened={menuStatus === MenuStatus.Closed ? false : true}
        template={"menu"}
      >
        <Popup
          visible={popUp}
          showTitle={true}
          title="מעבר למסך אחר"
          showCloseButton={false}
          width={300}
          height={180}
        >
          מעבר מהמסך הנוכחי לא תשמור נתונים במידה והוזנו!
          <ToolbarItem
            widget="dxButton"
            toolbar="bottom"
            location="after"
            options={closeButtonOptions}
          />
          <ToolbarItem
            widget="dxButton"
            toolbar="bottom"
            location="center"
            options={switchButtonOptions}
          />
        </Popup>
        <div className={"container"}>
          <ScrollView ref={scrollViewRef} className={"layout-body with-footer"}>
            <div className={"content"}>
              {React.Children.map(children, (item) => {
                return item.type !== Footer && item;
              })}
            </div>
            <div className={"content-block"}>
              {React.Children.map(children, (item) => {
                return item.type === Footer && item;
              })}
            </div>
          </ScrollView>
        </div>
        <Template name={"menu"}>
          <SideNavigationMenu
            compactMode={menuStatus === MenuStatus.Closed}
            selectedItemChanged={onNavigationChanged}
            openMenu={temporaryOpenMenu}
            onMenuReady={onMenuReady}
          ></SideNavigationMenu>
        </Template>
      </Drawer>
    </div>
  );
}

const MenuStatus = {
  Closed: 1,
  Opened: 2,
  TemporaryOpened: 3,
};
